import { useLocale, useTranslations } from 'next-intl';
import { formatDistanceToNow } from 'date-fns';
import { Modal } from '@/component/Modal';
import { Image } from '@/component/Image';
import { ReviewsStars } from '@/component/Reviews/ReviewsStars';
import { dateLocales } from '@/i18n';
import { clearHtml } from '@/utils/html';
import { Link } from '@/i18n/routing';
import { mapGtmItem, pushListingCard } from '@/utils/gtm';
import { useMediaQuery } from 'react-responsive';
import { getListingLocaleSlug } from '@/utils';

export function ReviewsModal({ item, pageName, ...props }) {
  const { author, createdAt, text, rating: stars, listing } = item;

  const locale = useLocale();
  const t = useTranslations();

  const bodyId = 'review-container';

  const markup = { __html: t.raw(clearHtml(text)) };

  const isMobile = useMediaQuery({ query: '(max-width: 639px)' });

  const pushGTM = () => pushListingCard(pageName, mapGtmItem(item.listing));

  const cardUrl = {
    href: `/listings/${getListingLocaleSlug(item.listing, locale)}`,
    target: isMobile ? '_self' : '_blank',
    onClick: pushGTM,
  };

  const header = (
    <div className="flex gap-[14px] items-center max-md:order-2 head-modal">
      <div className="h-11 w-11 rounded-full overflow-hidden">
        {author.avatar ? (
          <Image
            className="!h-auto object-cover"
            height={44}
            width={44}
            alt={author.name}
            title={author.name}
            src={author.avatar}
          />
        ) : (
          <span
            className={`rounded-full size-11 inline-flex items-center justify-center bg-slate-800 text-white`}
          >
            {author.name[0]}
          </span>
        )}
      </div>
      <div className="flex flex-col">
        <div
          className="text-base font-semibold flex gap-1 items-center"
          data-testid="room-reviews-header"
        >
          {author.name}
        </div>
        <div className="inline-flex gap-2 items-center">
          <ReviewsStars stars={stars} />
          <span className="text-slate-500 text-xs">
            {formatDistanceToNow(new Date(createdAt), {
              locale: dateLocales[locale],
              addSuffix: true,
            })}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      {...props}
      bodyId={bodyId}
      header={header}
      headerClass="max-md:[&>div]:hidden max-md:!py-1 !py-6"
      bodyClass="max-md:!px-6 !px-8"
    >
      <div className="!w-full [&>div]:!w-full">
        <div className="md:hidden mb-6">{header}</div>
        <Link
          {...cardUrl}
          className="text-slate-500 text-base font-normal underline leading-normal pb-6 inline-flex"
          prefetch={false}
        >
          {listing.name}
        </Link>
        <div
          className="text-slate-500 text-base"
          dangerouslySetInnerHTML={markup}
        />
      </div>
    </Modal>
  );
}
